import { FC } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from '@mui/material';

import LockIcon from 'icons/monochrome/lock.svg';
import { TariffUpgradeReason } from 'src/types';
import { SUPPORT_CONTACT_URL } from 'src/config-global';

type Props = {
  open: boolean;
  reason: TariffUpgradeReason | null;
  onClose: () => void;
};

export const UpgradePlanDialog: FC<Props> = ({ open, reason, onClose }) => {
  const { t } = useTranslation('', { keyPrefix: 'tariff.dialog' });

  const handleContactSupport = () => {
    window.open(SUPPORT_CONTACT_URL, '_blank', 'noopener,noreferrer');
  };

  const isLimit = reason?.type === 'limit';

  const title = isLimit ? t('limit-title') : t('feature-title');

  const description = (() => {
    if (!reason) return t('feature-description');
    if (reason.type === 'feature') {
      return t(`features.${reason.feature}`, { defaultValue: t('feature-description') });
    }
    if (reason.resource && reason.usage) {
      return t(`limits.${reason.resource}`, {
        current: reason.usage.current,
        limit: reason.usage.limit,
        defaultValue: t('limit-description'),
      });
    }
    return t('limit-description');
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component={LockIcon} sx={{ width: 22, height: 22, flexShrink: 0 }} />
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: 'column', gap: 1, '& > :not(:first-of-type)': { ml: 0 } }}
      >
        <Button variant="contained" color="primary" fullWidth onClick={handleContactSupport}>
          {t('contact-support')}
        </Button>
        <Button variant="text" color="inherit" fullWidth onClick={onClose}>
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
